/**
 * READING THE TERMINAL LOG — the Driver Events tab's half of a contract
 * that has no schema.
 *
 * `entry.metadata` is a JSON string, written as `{ ...body }` by whichever
 * client recorded the event. Adding a field to it costs nothing, which is
 * exactly why it has drifted: the log has carried a partner's refusal
 * reason, the minutes on a waiting clock, and the email of an administrator
 * acting as a driver for as long as those things have existed, and the tab
 * showed none of them — just the action's name in capitals and a date.
 *
 * On a dispute surface that is worse than it sounds. The office is asked
 * "why did you bill a no-show", "why does Booking.com have no arrival time
 * from you", "who was actually driving" — and the answer was sitting in the
 * log, unread, while the tab said `PARTNER EVENT REJECTED` and nothing else.
 *
 * So this module is the READ side, written next to the writes it mirrors:
 *
 *   PortalTripEventService        OVERTIME_STARTED / _ENDED / _REPORTED,
 *                                 PASSENGER_CALL_ATTEMPTED, and the portal
 *                                 attribution on all of them
 *   DriverOvertimeService         OVERTIME_APPROVED / OVERTIME_REJECTED
 *   PassengerTerminalController   NEW_EVENT, UPDATE_DRIVER, UPDATE_CAR,
 *                                 NOTIFY_PASSENGER, NOTIFY_DRIVER,
 *                                 CANT_FIND_DRIVER
 *   the partner driver-events     PARTNER_EVENT_SENT / _REJECTED
 *     consumer
 *   DriverEventsService           ASSIGNMENT_CHANGED (synthesised from the
 *                                 booking audit log, not stored)
 *
 * THREE RULES, because this is evidence and not a debug view.
 *
 *  1. AN OLD ROW MUST STILL READ. Nothing here requires a field. Every
 *     lookup is optional and every absent one is simply left out, so a row
 *     written before any of this existed renders as what it is — an action
 *     and a time — rather than as a list of blanks.
 *  2. NOTHING IS CLAIMED THAT IS NOT THERE. A missing position is only
 *     mentioned on the events that could have carried one (a driver's phone
 *     recorded it); saying "No location recorded" under the office's own
 *     approval would suggest a fix was sought and missed, which is untrue.
 *  3. AN UNKNOWN ACTION STILL APPEARS. A future writer that this file has
 *     never heard of gets its name, its time and its actor, and is never
 *     silently dropped.
 */
import getDriverStatusLabel from './getDriverStatusLabel';

/**
 * What each action MEANS, in the words the office would use.
 *
 * `NEW_EVENT` is deliberately absent: the row already prints the driver's
 * step underneath it (`getDriverStatusLabel`), and giving it a heading too
 * would say the same thing twice.
 */
const HEADINGS = {
  UPDATE_DRIVER: 'Driver changed from the terminal',
  UPDATE_CAR: 'Car changed from the terminal',
  NOTIFY_PASSENGER: 'Passenger told the driver had arrived',
  NOTIFY_DRIVER: 'Passenger said they were at the meeting point',
  CANT_FIND_DRIVER: 'Passenger could not find the driver',
  OVERTIME_STARTED: 'Waiting clock started',
  OVERTIME_ENDED: 'Waiting clock stopped',
  OVERTIME_REPORTED: 'Waiting reported by the driver',
  OVERTIME_APPROVED: 'Waiting approved',
  OVERTIME_REJECTED: 'Waiting rejected',
  PASSENGER_CALL_ATTEMPTED: 'Driver called the passenger',
  PARTNER_EVENT_SENT: 'Partner was told',
  PARTNER_EVENT_REJECTED: 'Partner refused it',
  ASSIGNMENT_CHANGED: 'Assignment changed',
};

/** The action as a reader should see it, unknown ones included. */
export const headingFor = (action) => HEADINGS[action] || String(action || '').replace(/_/g, ' ');

/**
 * Events whose metadata carries the position of the DRIVER'S OWN PHONE at
 * the moment of the tap. Only these say anything when the position is
 * missing — see rule 2. Anything else that happens to carry a real fix
 * still shows it (`showsPosition`), because a real coordinate is never
 * worth hiding.
 */
const DEVICE_POSITION_ACTIONS = new Set([
  'NEW_EVENT',
  'OVERTIME_STARTED',
  'OVERTIME_ENDED',
  'OVERTIME_REPORTED',
  'PASSENGER_CALL_ATTEMPTED',
]);

export const carriesDevicePosition = (action) => DEVICE_POSITION_ACTIONS.has(action);

/**
 * WHO DID THIS. Never absent — an event with nobody attached to it is not
 * evidence of anything.
 *
 * The one that matters most is `assumed_by_email`: an administrator signed
 * in AS a driver and tapped this. The driver did not. That is written down
 * (PortalTripEventService's `assumption`) and has never been shown, and it
 * is the first thing that would be argued about if it were ever disputed.
 */
export function actorFor(from, metadata = {}) {
  if (metadata.assumed_by_email) {
    return `${metadata.assumed_by_email}, signed in as the driver`;
  }

  if (metadata.portal_kind === 'driver') return 'Driver, in the app';
  if (metadata.portal_kind === 'operator') return 'Operator, in the app';
  if (metadata.reviewed_by) return `Office · ${metadata.reviewed_by}`;

  if (metadata.changed_by) {
    return metadata.changed_by === 'system' ? 'System' : `Office · ${metadata.changed_by}`;
  }

  if (from === 'Driver') return 'Driver';
  if (from === 'Passenger') return 'Passenger';

  // 'Agent' covers both a person in the office and the system acting on the
  // booking's behalf, and the row cannot tell which. It says the honest
  // thing rather than crediting a colleague who was not there.
  return 'Office or system';
}

/** Whole minutes, spelled out — "1 minute", never "1 minutes". */
const minutesText = (minutes) => (
  Number(minutes) === 1 ? '1 minute' : `${minutes} minutes`
);

/** Where the driver was kept waiting, in words rather than a field value. */
const STAGES = { pickup: 'at the pickup', dropoff: 'at the drop-off' };

/** What stopped a running clock, as the portal records the trigger. */
const TRIGGERS = {
  overtime_ended: 'the driver stopped it',
  departed_to_dropoff: 'departing to the drop-off',
  arrived_at_dropoff: 'arrival at the drop-off',
  no_show: 'the no-show report',
};

const detail = (label, value) => ({ label, value });

/** A person, a car, or null — from either shape UPDATE_DRIVER ever wrote. */
const describeDriver = (driver) => (
  driver ? [driver.full_name, driver.phone_number].filter(Boolean).join(' · ') || null : null
);

const describeCar = (car) => (
  car ? [car.type, car.color, car.plate_number].filter(Boolean).join(' · ') || null : null
);

/**
 * One side of an assignment change. `{ id: null }` is nobody; an id with no
 * name is a record that has since been deleted, and says so — the two must
 * never read the same, because one of them means the trip had no driver.
 */
const describeSide = (side) => {
  if (!side || !side.id) return 'nobody';
  return side.name || `a record no longer on file (${String(side.id).slice(0, 8)}…)`;
};

/**
 * A partner message, named the way the partner would name it: which of
 * their endpoints, and which step of the trip.
 */
function partnerDetails(metadata) {
  const lines = [];

  if (metadata.partner_id) lines.push(detail('Partner', String(metadata.partner_id)));

  if (metadata.type === 'DRIVER_ASSIGNED') {
    lines.push(detail('Message', 'Driver and car'));
  } else if (metadata.state !== undefined && metadata.state !== null) {
    lines.push(detail('Message', getDriverStatusLabel(Number(metadata.state))));
  } else if (metadata.type) {
    lines.push(detail('Message', String(metadata.type).replace(/_/g, ' ')));
  }

  if (metadata.partner_booking_id) {
    lines.push(detail('Their reference', String(metadata.partner_booking_id)));
  }

  return lines;
}

/**
 * Everything the row should say beyond its heading, as label/value pairs.
 * Only what the metadata actually holds — an absent field produces no line.
 */
export function detailsFor(action, rawMetadata) {
  const metadata = rawMetadata || {};

  if (metadata.metadata_unreadable) {
    // The API could not parse this row's metadata. Say that, rather than
    // render an empty row that reads like "nothing was recorded".
    return [detail('', 'This event was recorded, but its details could not be read.')];
  }

  const lines = [];

  switch (action) {
    case 'OVERTIME_STARTED':
    case 'OVERTIME_ENDED':
    case 'OVERTIME_REPORTED':
    case 'OVERTIME_APPROVED':
    case 'OVERTIME_REJECTED': {
      if (metadata.stage) lines.push(detail('Waiting', STAGES[metadata.stage] || metadata.stage));
      if (metadata.minutes !== undefined && metadata.minutes !== null) {
        lines.push(detail('Minutes', minutesText(metadata.minutes)));
      }
      if (metadata.method === 'manual') lines.push(detail('Counted', 'by the driver, after the fact'));
      if (metadata.trigger) {
        lines.push(detail('Stopped by', TRIGGERS[metadata.trigger] || String(metadata.trigger).replace(/_/g, ' ')));
      }
      if (metadata.stopped_running_timer) {
        lines.push(detail('Note', 'The review is what stopped the clock, not the driver.'));
      }
      if (metadata.note) lines.push(detail('Driver\'s note', metadata.note));
      break;
    }

    case 'PARTNER_EVENT_SENT':
      lines.push(...partnerDetails(metadata));
      break;

    case 'PARTNER_EVENT_REJECTED': {
      lines.push(...partnerDetails(metadata));
      // The reason in the partner's own words. This is the single most
      // useful line on the whole tab when a partner says they were never
      // told something, and it has been written since the day rejections
      // were recorded.
      if (metadata.reason) lines.push(detail('They said', String(metadata.reason)));
      if (metadata.status) lines.push(detail('Status', String(metadata.status)));
      lines.push(detail('', 'Nothing retries this: the partner does not have it.'));
      break;
    }

    case 'UPDATE_DRIVER': {
      const assigned = describeDriver(metadata.assigned);
      // Rows written before both sides were recorded carry the REPLACED
      // driver at the top level and nothing else. Read as what it was.
      const previous = describeDriver(metadata.previous)
        || (metadata.assigned ? null : describeDriver(metadata));

      if (previous) lines.push(detail('Was', previous));
      if (assigned) lines.push(detail('Now', assigned));
      break;
    }

    case 'UPDATE_CAR': {
      const assigned = describeCar(metadata.assigned);
      const previous = describeCar(metadata.previous)
        || (metadata.assigned ? null : describeCar(metadata));

      if (previous) lines.push(detail('Was', previous));
      if (assigned) lines.push(detail('Now', assigned));
      break;
    }

    case 'ASSIGNMENT_CHANGED': {
      (metadata.changes || []).forEach((change) => {
        lines.push(detail(change.label || change.field, `${describeSide(change.from)} → ${describeSide(change.to)}`));
      });
      break;
    }

    default:
      break;
  }

  return lines;
}

/* --- keeping the tab readable ------------------------------------------
 * A trip can produce a long run of rows that are individually worth having
 * and collectively worth nothing to look at: every partner message we got
 * an acknowledgement for, and — the moment anything starts publishing it —
 * every live-location ping. The office is looking for the moment something
 * went WRONG, and it must not have to scroll past a hundred confirmations
 * to find it.
 *
 * So a consecutive run of those collapses to one line with a count, opened
 * on a click. A refusal is never collapsed: it is precisely the exception
 * being hunted for, and it stands on its own row.
 */

/** The driver step that is a position, not a step. Never a row of its own. */
const LIVE_LOCATION_STATE = 6;

/** Which collapsible run an event belongs to, or null to stand alone. */
export function backgroundKindOf(event) {
  const action = event?.entry?.action;

  if (action === 'PARTNER_EVENT_SENT') return 'partner';

  if (action === 'NEW_EVENT' && Number(event?.entry?.metadata?.event_type) === LIVE_LOCATION_STATE) {
    return 'location';
  }

  return null;
}

const GROUP_HEADINGS = {
  partner: (count) => `${count} partner messages accepted`,
  location: (count) => `${count} location updates`,
};

/**
 * A run shorter than this is not worth hiding — a single "Show 1" is more
 * work for the reader than the row it hides.
 */
const MIN_RUN_TO_COLLAPSE = 2;

/**
 * The timeline as rows to render: one per event, plus a header row in front
 * of each collapsed run.
 *
 * `openGroups` is a Set of group keys. A group that is open keeps its header
 * (so it can be closed again) and is followed by its own events, each with
 * the index it has in `events` — so a row's map, its test id and its key do
 * not move when a group above it is opened.
 */
export function driverEventRows(events = [], openGroups = new Set()) {
  const rows = [];
  let cursor = 0;

  while (cursor < events.length) {
    const kind = backgroundKindOf(events[cursor]);

    if (!kind) {
      rows.push({ kind: 'event', event: events[cursor], index: cursor });
      cursor += 1;
      continue; // eslint-disable-line no-continue
    }

    let end = cursor;
    while (end < events.length && backgroundKindOf(events[end]) === kind) end += 1;

    const run = [];
    for (let at = cursor; at < end; at += 1) {
      run.push({ kind: 'event', event: events[at], index: at });
    }

    if (run.length < MIN_RUN_TO_COLLAPSE) {
      rows.push(...run);
    } else {
      const key = `${kind}-${cursor}`;
      const open = openGroups.has(key);

      rows.push({
        kind: 'group',
        key,
        group: kind,
        open,
        count: run.length,
        heading: GROUP_HEADINGS[kind](run.length),
        first: events[cursor],
        last: events[end - 1],
      });

      if (open) rows.push(...run);
    }

    cursor = end;
  }

  return rows;
}
